import type { DisciplineDetails } from '@ts-discipline/interfaces'
import type { ApiResponse } from '@utils/api/run-api-get'

import { useDisciplineStore } from '@stores/discipline'
import { useCommonStore } from '@stores/common'

import { useAdminRoutes } from '@composables/routes/admin-route.hook'

import { getAxiosErrorMessage } from '@helpers/axios'

import { useToast } from '@UI/Toast/ToastProvider'

interface DisciplineDetailsApiOptions {
  disciplineId: string | number
  /** Leave the layout loader alone: the caller shows its own (the discipline page loader). */
  silent?: boolean
  onStart?: () => void
  onSuccess?: (details: DisciplineDetails) => void
  onError?: (message: string) => void
  onFinish?: () => void
}

/** `GET /admin-api/disciplines/{discipline}/details` — the one entity read every discipline tab shares. */
export const useDisciplineDetailsApi = async (
  options: DisciplineDetailsApiOptions,
): Promise<DisciplineDetails | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const disciplineStore = useDisciplineStore()
  const commonStore = useCommonStore()

  const url = useAdminRoutes('admin.disciplines.api.details', { discipline_id: options.disciplineId })

  const setLoading = (value: boolean): void => {
    if (!options.silent) commonStore.setLoading(value)
  }

  options.onStart?.()
  setLoading(true)

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options.onError?.(msg)
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url)
    const details: DisciplineDetails = data.data

    disciplineStore.setDetails(details)
    options.onSuccess?.(details)

    return details
  } catch (err: unknown) {
    const msg = getAxiosErrorMessage(err)
    toast.error(msg)
    options.onError?.(msg)

    return null
  } finally {
    setLoading(false)
    options.onFinish?.()
  }
}
